import { useState } from "react";
import { Button, Popconfirm, Space, Table, Tabs, Tag, Typography } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  QrcodeOutlined,
  SafetyCertificateOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { ALL_SECTION_KEYS } from "../../access/sections";
import { OperatorModal } from "./OperatorModal";
import { RoleModal } from "./RoleModal";
import { useAdminAccess } from "./useAdminAccess";
import { CmsOperatorInfo, CmsRoleInfo } from "./types";

// Служебные учётки: cms_app_username — сервисная учётка приложения (через неё
// cms-app ходит в Keycloak Admin API, её удаление ломает раздел), cms-admin —
// бутстрап-админ. В списке не показываем, чтобы их нельзя было тронуть из CMS.
const HIDDEN_LOGINS = ["cms-admin", "cms_app_username"];

export const AdminAccess = () => {
  const api = useAdminAccess();

  const [operatorModalOpen, setOperatorModalOpen] = useState(false);
  const [editingOperator, setEditingOperator] = useState<CmsOperatorInfo>();
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<CmsRoleInfo>();

  const visibleOperators = api.operators.filter(
    (o) => !HIDDEN_LOGINS.includes((o.login || "").toLowerCase())
  );

  // Последнего админа снять нельзя — бэкенд вернёт 409, поэтому и кнопку гасим.
  // Считаем по полному списку: скрытые админы тоже админы.
  const isLastAdmin = api.operators.filter((o) => o.isAdmin).length <= 1;

  const closeOperatorModal = () => {
    setOperatorModalOpen(false);
    setEditingOperator(undefined);
  };
  const closeRoleModal = () => {
    setRoleModalOpen(false);
    setEditingRole(undefined);
  };

  const operatorsTab = (
    <>
      <Space style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingOperator(undefined);
            setOperatorModalOpen(true);
          }}
        >
          Создать пользователя
        </Button>
      </Space>
      <Table<CmsOperatorInfo>
        dataSource={visibleOperators}
        rowKey="keycloakId"
        loading={api.loading}
        pagination={false}
        scroll={{ x: "max-content" }}
      >
        <Table.Column<CmsOperatorInfo>
          dataIndex="login"
          title="Логин"
          render={(value, record) => (
            <Space>
              <Typography.Text strong style={{ whiteSpace: "nowrap" }}>
                {value}
              </Typography.Text>
              {record.isAdmin && <Tag color="gold">админ</Tag>}
              {record.twoFactorEnabled && <Tag color="green">2FA</Tag>}
              {!record.enabled && <Tag color="red">отключен</Tag>}
              {!record.managed && !record.isAdmin && <Tag>без доступов</Tag>}
            </Space>
          )}
        />
        <Table.Column<CmsOperatorInfo>
          dataIndex="fullName"
          title="ФИО"
          render={(v) => v || "—"}
        />
        <Table.Column<CmsOperatorInfo>
          dataIndex="description"
          title="Описание"
          render={(v) => v || "—"}
        />
        <Table.Column<CmsOperatorInfo>
          dataIndex="roleName"
          title="Роль"
          render={(v) => v || "—"}
        />
        <Table.Column<CmsOperatorInfo>
          key="actions"
          title="Действия"
          render={(_, record) => (
            <Space>
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => {
                  setEditingOperator(record);
                  setOperatorModalOpen(true);
                }}
              >
                Изменить
              </Button>
              {record.managed && (
                <Popconfirm
                  title="Снять доступы? Учётная запись сохранится."
                  okText="Снять"
                  cancelText="Отмена"
                  onConfirm={() => api.revokeOperator(record.keycloakId)}
                >
                  <Button size="small" icon={<StopOutlined />}>
                    Снять доступ
                  </Button>
                </Popconfirm>
              )}
              {record.twoFactorEnabled && (
                <Popconfirm
                  title="Сбросить двухфакторку? Пользователь при следующем входе заново отсканирует QR-код."
                  okText="Сбросить"
                  cancelText="Отмена"
                  onConfirm={() => api.resetTotp(record.keycloakId)}
                >
                  <Button size="small" icon={<QrcodeOutlined />}>
                    Сбросить 2FA
                  </Button>
                </Popconfirm>
              )}
              {record.isAdmin ? (
                <Popconfirm
                  title="Снять права администратора? Останется доступ только к разделам своей роли."
                  okText="Снять"
                  cancelText="Отмена"
                  disabled={isLastAdmin}
                  onConfirm={() => api.revokeAdmin(record.login)}
                >
                  <Button
                    size="small"
                    icon={<SafetyCertificateOutlined />}
                    disabled={isLastAdmin}
                    title={
                      isLastAdmin
                        ? "Это последний администратор — сначала назначьте другого"
                        : undefined
                    }
                  >
                    Убрать админа
                  </Button>
                </Popconfirm>
              ) : (
                <Popconfirm
                  title="Выдать права администратора? Пользователь получит доступ ко всем разделам и управлению доступами."
                  okText="Выдать"
                  cancelText="Отмена"
                  onConfirm={() => api.grantAdmin(record.login)}
                >
                  <Button size="small" icon={<SafetyCertificateOutlined />}>
                    Сделать админом
                  </Button>
                </Popconfirm>
              )}
              {!record.isAdmin && (
                <Popconfirm
                  title="Удалить пользователя полностью (включая вход)?"
                  okText="Удалить"
                  cancelText="Отмена"
                  onConfirm={() => api.deleteOperator(record.keycloakId)}
                >
                  <Button size="small" danger icon={<DeleteOutlined />}>
                    Удалить
                  </Button>
                </Popconfirm>
              )}
            </Space>
          )}
        />
      </Table>
    </>
  );

  const rolesTab = (
    <>
      <Space style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingRole(undefined);
            setRoleModalOpen(true);
          }}
        >
          Создать роль
        </Button>
      </Space>
      <Table<CmsRoleInfo>
        dataSource={api.roles}
        rowKey="id"
        loading={api.loading}
        pagination={false}
      >
        <Table.Column<CmsRoleInfo> dataIndex="name" title="Название" />
        <Table.Column<CmsRoleInfo>
          dataIndex="description"
          title="Описание"
          render={(v) => v || "—"}
        />
        <Table.Column<CmsRoleInfo>
          dataIndex="sections"
          title="Разделы"
          render={(sections: string[]) =>
            sections.length
              ? `${sections.length} из ${ALL_SECTION_KEYS.length}`
              : "нет"
          }
        />
        <Table.Column<CmsRoleInfo>
          key="actions"
          title="Действия"
          width={240}
          render={(_, record) => (
            <Space>
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => {
                  setEditingRole(record);
                  setRoleModalOpen(true);
                }}
              >
                Изменить
              </Button>
              <Popconfirm
                title="Удалить роль? Пользователи с этой ролью останутся без доступов."
                okText="Удалить"
                cancelText="Отмена"
                onConfirm={() => api.deleteRole(record.id)}
              >
                <Button size="small" danger icon={<DeleteOutlined />}>
                  Удалить
                </Button>
              </Popconfirm>
            </Space>
          )}
        />
      </Table>
    </>
  );

  return (
    <div style={{ padding: 24 }}>
      <Typography.Title level={4}>Пользователи и роли</Typography.Title>
      <Tabs
        items={[
          { key: "operators", label: "Пользователи", children: operatorsTab },
          { key: "roles", label: "Роли", children: rolesTab },
        ]}
      />

      <OperatorModal
        open={operatorModalOpen}
        saving={api.saving}
        roles={api.roles}
        operator={editingOperator}
        onCancel={closeOperatorModal}
        onCreate={async (values) => {
          const ok = await api.createOperator(values);
          if (ok) closeOperatorModal();
          return ok;
        }}
        onUpdate={async (keycloakId, values) => {
          const ok = await api.updateOperator(keycloakId, values);
          if (ok) closeOperatorModal();
          return ok;
        }}
      />

      <RoleModal
        open={roleModalOpen}
        saving={api.saving}
        role={editingRole}
        onCancel={closeRoleModal}
        onSubmit={async (values) => {
          const ok = editingRole
            ? await api.updateRole(editingRole.id, values)
            : await api.createRole(values);
          if (ok) closeRoleModal();
          return ok;
        }}
      />
    </div>
  );
};
